import React, { useEffect, useState } from "react";
import "./expenseManagementStyle.css";
import { useNavigate } from "react-router-dom";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { BiEditAlt } from "react-icons/bi";
import { PiEye } from "react-icons/pi";
import dayjs from "dayjs";

// import LocationFilter from './location_filter/LocationFilter';

// import { delete_location_by_id, get_locations_with_pagination } from '../../../../api/locationApis';
import { ErrorToast, SuccessToast } from "../../../toast/toasts";
import CustomDeleteConfirmModal from "../../../components/custom_delete_confirm_modal/CustomDeleteConfirmModal";
import CustomBottomPagination from "../../../components/custom_bottom_pagination/CustomBottomPagination";
import CustomTable from "../../../components/custom_table/CustomTable";
import { LocalStorageServices } from "../../../services/localStorageServices";
import ExpenseManagementFilter from "./expense_management_filter/ExpenseManagementFilter";
import {
  delete_expense,
  get_expenses,
} from "../../../api/expenseManagementApis";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";

function ExpenseManagement() {
  const navigate = useNavigate();

  // Base route URL based on user role
  const baseRouteUrl = `/ecrs_portal/${LocalStorageServices.getUserRole()}/ExpenseManagement`;
  const globalFilter = useSelector((state) => state.expenseFilter);

  // State management
  const [expenseList, setExpenseList] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItemsPerPage, setTotalItemsPerPage] = useState(10);
  // const [searchFilter, setSearchFilter] = useState({
  //     expense_head_id: "",
  //     month_year: dayjs().format("YYYY-MM"),
  // });
  const [selectedId, setSelectedId] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);

  // Fetch data whenever page number or items per page changes
  useEffect(() => {
    getExpenses(pageNumber, globalFilter);
  }, [pageNumber, totalItemsPerPage, globalFilter]);

  // Fetch location list with pagination and optional filter
  const [isLoading, setIsLoading] = useState(true);

  const getExpenses = (page, filterValue) => {
    const formData = new FormData();
    formData.append("page", page);
    formData.append("pageItem_count", totalItemsPerPage);

    if (filterValue) {
      Object.entries(filterValue).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }
    get_expenses(formData)
      .then((response) => {
        setExpenseList(response.data.data.data);
        setIsLoading(false);
        setTotalPages(response.data.data.last_page);
        setTotalExpense(response.data.current_month_total || 0);
      })
      .catch((error) => {
        console.error("Error fetching locations:", error);
      });
  };

  // Privilege helpers
  const getPrivilegesDetails = () =>
    JSON.parse(localStorage.getItem("privileges_details") || "[]");

  const hasPrivilege = (privilegeName) =>
    getPrivilegesDetails().some(
      (item) =>
        item.module_name === "Expense Management" &&
        item.submodules?.["Expense Management"]?.privileges?.some(
          (priv) => priv.name === privilegeName
        )
    );

  //  below if privileges are needed
  const canView = hasPrivilege("View");
  const canAdd = hasPrivilege("Add");
  const canEdit = hasPrivilege("Edit");
  const canDelete = hasPrivilege("Delete");

  // Table columns
  const columns = [
    { title: "#", dataIndex: "key", key: "key" },
    { title: "Expense Head", dataIndex: "expense_head", key: "expense_head" },
    { title: "Amount", dataIndex: "amount", key: "amount" },
    { title: "Month", dataIndex: "month_year", key: "month_year" },
    // { title: 'Created At', dataIndex: 'created_at', key: 'created_at' },
    { title: "Remark", dataIndex: "remark", key: "remark" },

    // (canView || canEdit || canDelete)
    true && {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      action: (row) => {
        // current month and year in format YYYY-MM
        // Commented on 26-11-2025
        // YYYY-MM changed to MMM-YYYY (2025-11 to Nov-2025)

        // const currentMonthYear = dayjs().format('YYYY-MM'); // Old
        const currentMonthYear = dayjs().format("MMM-YYYY"); // New

        const isCurrentMonth = row.month_year === currentMonthYear;

        return (
          <div className="expenseManagement__action_buttons">
            {/* View always enabled */}
            {canView && (
              <PiEye
                className="locations_action_view"
                onClick={() =>
                  navigate(`${baseRouteUrl}/${row.id}/view_expense`)
                }
              />
            )}

            {/* Edit enabled only for current month */}
            {canEdit && (
              <BiEditAlt
                // className="locations_action_edit"
                // onClick={() => navigate(`${baseRouteUrl}/${row.id}/update_expense`)}
                className={`locations_action_edit ${
                  !isCurrentMonth ? "disabled-icon" : ""
                }`}
                onClick={() => {
                  if (isCurrentMonth) {
                    navigate(`${baseRouteUrl}/${row.id}/update_expense`);
                  }
                }}
                style={{
                  opacity: isCurrentMonth ? 1 : 0.4,
                  cursor: isCurrentMonth ? "pointer" : "not-allowed",
                }}
              />
            )}

            {/* Delete */}
            {canDelete && (
              <MdOutlineDeleteOutline
                // className="locations_action_delete"
                // onClick={() => {
                //     setSelectedId(row.id);
                //     setDeleteConfirmation(true);
                // }}
                className={`locations_action_delete ${
                  !isCurrentMonth ? "disabled-icon" : ""
                }`}
                onClick={() => {
                  if (isCurrentMonth) {
                    setSelectedId(row.id);
                    setDeleteConfirmation(true);
                  }
                }}
                style={{
                  opacity: isCurrentMonth ? 1 : 0.4,
                  cursor: isCurrentMonth ? "pointer" : "not-allowed",
                }}
              />
            )}
          </div>
        );
      },
    },
  ].filter(Boolean); // removes false/null entries

  // Delete location
  const deleteExpense = () => {
    if (!selectedId) return;

    delete_expense(selectedId)
      .then((response) => {
        if (response.status === 200) {
          handleDeleteModalClose(false);
          SuccessToast(response.data.message);
          getExpenses(pageNumber, globalFilter);
        }
      })
      .catch((error) => {
        handleDeleteModalClose(false);
        ErrorToast(error?.data?.message || "Failed to delete location");
      });
  };

  // Close delete confirmation modal
  const handleDeleteModalClose = (isClose) => {
    setDeleteConfirmation(isClose);
    setSelectedId(null);
  };

  // Prepare data for table
  const tableData = () =>
    expenseList?.map((item, index) => ({
      key: pageNumber * totalItemsPerPage - (totalItemsPerPage - (index + 1)),
      id: item.expense_history_id,
      expense_head: item?.expense_head?.ExpenseName,
      amount: item.amount,
      remark: item.description,
      created_at: item?.created_at,
      month_year: item?.month_year,
    }));

  // Pagination handlers
  const handlePageChange = (page) => setPageNumber(page);
  const handleItemsPerPageChange = (count) => setTotalItemsPerPage(count);

  // Search filter handler
  const handleSearchFilter = (value) => {
    // setSearchFilter(value);
    setPageNumber(1);
    getExpenses(1, value);
  };

  return (
    <div className="expenseManagement_module_main_container">
      <p className="expenseManagement_title">Expense Management</p>

      {/* Filter and Add */}
      <div className="expenseManagement_module_filter">
        <ExpenseManagementFilter
          handlePageChange={handlePageChange}
          handleItemsPerPageChange={handleItemsPerPageChange}
        />
        {canAdd && (
          <button
            className="expenseManagement_add_button"
            onClick={() => navigate(`${baseRouteUrl}/create_new_expense`)}
          >
            Add New
          </button>
        )}
      </div>

      {/* Table */}
      <div className="expenseManagement_table_main_container">
        <CustomTable columns={columns} rows={tableData()} loading={isLoading} />
      </div>
      {/* SUMMARY */}
      <Box className="expense_report_summary">
        <strong>Total Expense: ₹{totalExpense}</strong>
      </Box>

      {/* Pagination */}
      <CustomBottomPagination
        total_items_per_page={totalItemsPerPage}
        total_page_count={totalPages}
        current_page={pageNumber}
        on_change_value={handlePageChange}
        on_page_count_update={handleItemsPerPageChange}
      />

      {/* Delete Confirmation Modal */}
      <CustomDeleteConfirmModal
        module_name="expense"
        open_modal={deleteConfirmation}
        close_modal={handleDeleteModalClose}
        confirm_function={deleteExpense}
      />
    </div>
  );
}

export default ExpenseManagement;
